import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Changelog de Melhorias da HostVSL
 * Histórico de atualizações e melhorias implementadas
 * 
 * IMPORTANTE: Adicionar entrada sempre que implementar:
 * - Novo PRD
 * - Melhoria de UX/UI
 * - Refatoração significativa
 * - Correção de bugs importantes
 */

export interface ChangelogEntry {
  date: string;
  title: string;
  description: string;
  affectedAreas: string[];
  type: 'feature' | 'improvement' | 'fix' | 'refactor';
  prdReference?: string;
}

export const changelog: ChangelogEntry[] = [
  // Janeiro 2026
  {
    date: '2026-01-04',
    get title() { return translateInterfaceText("Documentação - Padronização Cores #1D4FFF"); },
    get description() { return translateInterfaceText("Atualização de toda documentação para usar Azul Host (#1D4FFF) como cor primária única. Corrigidas inconsistências entre DESIGN_SYSTEM.md, FEATURE_CONFIGS_STANDARD.md, PWA_README.md e colors.ts."); },
    affectedAreas: ['Documentation', 'Design System', 'Colors'],
    type: 'improvement',
  },
  {
    date: '2026-01-04',
    get title() { return translateInterfaceText("VideoControlsTab - Simplificação de Labels"); },
    get description() { return translateInterfaceText("Labels simplificados: \"Controle de Volume\" → \"Volume\", \"Controle de Velocidade\" → \"Velocidade\", \"Esconder no Início\" → \"Esconder Início\"."); },
    affectedAreas: ['VideoControlsTab'],
    type: 'improvement',
  },
  {
    date: '2026-01-04',
    get title() { return translateInterfaceText("AutoplayStyleSection - Accordion Aberto por Padrão"); },
    get description() { return translateInterfaceText("Accordion de Estilo Avançado agora inicia aberto por padrão. Texto atualizado de \"overlay\" para \"Autoplay\"."); },
    affectedAreas: ['AutoplayStyleSection'],
    type: 'improvement',
  },
  {
    date: '2026-01-04',
    get title() { return translateInterfaceText("ProgressBarConfig - Remoção Borda Extra"); },
    get description() { return translateInterfaceText("Removido SectionCard wrapper do preview da barra de progresso para visual mais limpo."); },
    affectedAreas: ['ProgressBarConfig'],
    type: 'improvement',
  },
  // Dezembro 2025
  {
    date: '2025-12-31',
    get title() { return translateInterfaceText("Teste de Hook - Navegação Corrigida"); },
    get description() { return translateInterfaceText("Corrigido bug onde isFinalStep calculava incorretamente, fazendo o botão Iniciar Teste aparecer na etapa 2. Alterado de currentStep === totalSteps - 1 para currentStep === totalSteps."); },
    affectedAreas: ['HookTrackWizardFooter'],
    type: 'fix',
  },
  {
    date: '2025-12-31',
    get title() { return translateInterfaceText("Teste de Hook - Padronização com Teste A/B"); },
    get description() { return translateInterfaceText("Implementado drag-and-drop na distribuição de tráfego, badge de ordem (#1, #2, #3) nos cards de intros, e cards sortáveis com slider Radix idênticos ao Teste A/B."); },
    affectedAreas: ['TrafficDistributionStep', 'SortableHookTrackCard', 'HookTrackCardOverlay', 'IntrosSelectionStep', 'wizardStore'],
    type: 'feature',
    prdReference: 'PRD Padronização Teste Hook com A/B',
  },
  {
    date: '2025-12-31',
    get title() { return translateInterfaceText("Teste A/B - Remoção Badge Contagem"); },
    get description() { return translateInterfaceText("Removido chip X vídeos selecionados do título na etapa de seleção de vídeos. Layout mais limpo e minimalista."); },
    affectedAreas: ['VideosSelectionStep'],
    type: 'improvement',
  },
  {
    date: '2025-12-30',
    get title() { return translateInterfaceText("Teste A/B - Wizard Premium com Drag-and-Drop"); },
    get description() { return translateInterfaceText("Wizard completo de criação de Teste A/B com 3 etapas: seleção de vídeos, distribuição de tráfego com drag-and-drop usando DnD-Kit, e cards sortáveis com badge de ordem e slider Radix."); },
    affectedAreas: ['VideosSelectionStep', 'ABTestTrafficDistributionStep', 'SortableABTestVideoCard', 'ABTestVideoCardOverlay', 'abTestWizardStore'],
    type: 'feature',
    prdReference: 'PRD Teste A/B Premium',
  },
  {
    date: '2025-12-07',
    get title() { return translateInterfaceText("Parceiros — Página Premium 10/10"); },
    get description() { return translateInterfaceText("Redesign completo da página Parceiros seguindo PRD premium. Cards com altura fixa 212px, hover scale + blur, skeleton loading, fade-in animation, ícone chevron animado, acessibilidade completa."); },
    affectedAreas: ['Partners', 'PartnerCard', 'PartnerCardSkeleton'],
    type: 'feature',
    prdReference: 'PRD Aba Parceiros Premium 10/10',
  },
  {
    date: '2025-12-07',
    get title() { return translateInterfaceText("Sidebar — Nova Aba Parceiros"); },
    get description() { return translateInterfaceText("Adicionada aba \"Parceiros\" com ícone Handshake no menu lateral desktop (Sidebar) e mobile (DrawerContent), posicionada após \"Membros\"."); },
    affectedAreas: ['Sidebar', 'DrawerContent', 'Navigation'],
    type: 'feature',
  },
  {
    date: '2025-12-07',
    get title() { return translateInterfaceText("Upload Modal — Correção Borda Seleção"); },
    get description() { return translateInterfaceText("Corrigida borda azul do vídeo selecionado que estava sendo cortada no modal de upload. Adicionado padding ao grid e substituído ring por border sólida."); },
    affectedAreas: ['UploadQueueModal', 'UploadItemCard'],
    type: 'fix',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Sistema de Documentação Dinâmica"); },
    get description() { return translateInterfaceText("Implementação de registry centralizado para documentação auto-atualizada. Rotas, stores, componentes e design tokens agora são documentados dinamicamente."); },
    affectedAreas: ['Documentation', 'registry.ts', 'changelog.ts'],
    type: 'feature',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("VideoSelector — Dropdown Alinhado Premium"); },
    get description() { return translateInterfaceText("Dropdown de seleção de vídeo agora herda largura exata do botão trigger usando CSS variable do Radix. Aplicados estilos premium: border #E6E8EC, radius 10px, shadow, altura 48px por item."); },
    affectedAreas: ['VideoSelectorV2', 'Header'],
    type: 'improvement',
    prdReference: 'PRD Dropdown Seleção de Vídeo',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Tipografia Premium — Font-weight 500"); },
    get description() { return translateInterfaceText("Números principais em toda área de Métricas ajustados para font-weight 500, criando hierarquia visual mais clara e premium."); },
    affectedAreas: ['MetricCard', 'PrimeTimesGrid', 'ViewsChart', 'TrafficRetentionChart'],
    type: 'improvement',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Recurso Horários — Premium 10/10"); },
    get description() { return translateInterfaceText("Heatmap refinado com cores suavizadas, legenda de intensidade e tooltips premium com sombra unificada."); },
    affectedAreas: ['PrimeTimeCell', 'PrimeTimesGrid', 'PremiumPeakCards'],
    type: 'improvement',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Métricas — Padrão Visual Definitivo"); },
    description: 'Tooltips com sombra 0px 14px 28px rgba(0,0,0,0.03), gráficos com stroke 2.4px e glow, tabs com background #4B7BFF.',
    affectedAreas: ['Metrics', 'ViewsTab', 'RetentionTab', 'TrafficTab'],
    type: 'improvement',
    prdReference: 'PRD Métricas Premium 10/10',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Dashboard — 6 Cards Premium"); },
    get description() { return translateInterfaceText("Dashboard redesenhado com 6 cards executivos: Overview, TopVSL, PitchHealth, Engagement, ConversionsRevenue, Audience. Altura fixa 260px, skeletons com shimmer."); },
    affectedAreas: ['Dashboard', 'StatsCard', 'DashboardSkeleton'],
    type: 'feature',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Dark Members 2.0 — Template Premium"); },
    get description() { return translateInterfaceText("Novo template de área de membros com hero banner 1920×700, módulos/bônus em grid, progress tracking persistente, CTAs por vídeo."); },
    affectedAreas: ['DarkMembersEditor', 'DarkMembersPlaylist', 'DarkMembersPlaylistView'],
    type: 'feature',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Prime Video Dark — Template Substituindo Netflix"); },
    get description() { return translateInterfaceText("Novo template Prime Video Dark com hero banner, grid de aulas com paginação, modal de reprodução com navegação entre episódios."); },
    affectedAreas: ['PlaylistNetflixTemplate', 'PlaylistNetflixEditor'],
    type: 'feature',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Clean 2.0 — Template Horizontal"); },
    get description() { return translateInterfaceText("Template horizontal estilo Wistia/Vimeo com player centralizado, grid responsivo 4/2/1 colunas, autoplay sequencial com countdown."); },
    affectedAreas: ['PlaylistClean2Template', 'AutoplayOverlay'],
    type: 'feature',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Playlists — Sistema Unificado de Edição"); },
    get description() { return translateInterfaceText("Editor unificado inline para todos os templates, eliminando navegação entre páginas. Configurações específicas aparecem inline por template."); },
    affectedAreas: ['PlaylistUnifiedEditor', 'PlaylistLightEditor', 'PlaylistNetflixEditor'],
    type: 'refactor',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Mobile Menu — Layout Final"); },
    get description() { return translateInterfaceText("Menu mobile com overlay click blocking, animações sincronizadas, plan card com progress bar animado, navegação reorganizada."); },
    affectedAreas: ['MobileMenu', 'PlanUsageSectionMobile'],
    type: 'improvement',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Sidebar — Posicionamento Fixo"); },
    get description() { return translateInterfaceText("Sidebar desktop com position fixed para estabilidade, main content com margin-left compensatório. Layout Apple-like."); },
    affectedAreas: ['Sidebar', 'Layout'],
    type: 'improvement',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Cloaker — Sistema de Regras"); },
    get description() { return translateInterfaceText("Cards de regras com footer mostrando data de criação, toggle de ativação, modais de seleção de vídeo premium."); },
    affectedAreas: ['VSLCloaker', 'CloakerRuleCard', 'VideoCardSelected'],
    type: 'feature',
  },
  {
    date: '2025-12-06',
    get title() { return translateInterfaceText("Embed Code Modal — Premium"); },
    get description() { return translateInterfaceText("Modal de código embed com tabs JavaScript/iFrame, cards de código com 160px altura fixa, botões de cópia integrados."); },
    affectedAreas: ['EmbedCodeModal'],
    type: 'improvement',
  },
];

/**
 * Retorna as últimas N entradas do changelog
 */
export function getRecentChanges(count: number = 5): ChangelogEntry[] {
  return changelog.slice(0, count);
}

/**
 * Retorna entradas filtradas por tipo
 */
export function getChangesByType(type: ChangelogEntry['type']): ChangelogEntry[] {
  return changelog.filter(entry => entry.type === type);
}

/**
 * Retorna entradas que afetam um componente específico
 */
export function getChangesAffecting(component: string): ChangelogEntry[] {
  return changelog.filter(entry => 
    entry.affectedAreas.some(area => 
      area.toLowerCase().includes(component.toLowerCase())
    )
  );
}

/**
 * Retorna entradas por data (mais recentes primeiro)
 */
export function getChangesByDate(startDate: string, endDate?: string): ChangelogEntry[] {
  return changelog.filter(entry => {
    const entryDate = entry.date;
    if (endDate) {
      return entryDate >= startDate && entryDate <= endDate;
    }
    return entryDate >= startDate;
  });
}
